import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { fetchApi } from "./index";
import { useToast } from "@/components/ui/use-toast";

// Mirrors LibraryAnswerView in somethings-api/src/services/mentor-connect-library.ts.
export interface ConnectLibraryAnswer {
  libraryAnswerId: string;
  questionId: string;
  audioS3Key: string;
  audioUrl: string;
  durationMs: number;
  transcript: string | null;
  recordedAt: string;
}

interface ConnectLibraryResponse {
  library: ConnectLibraryAnswer[];
}

interface PresignedUrlResponse {
  audio_s3_key: string;
  presigned_url: string;
  content_type: string;
}

interface CommitResponse {
  library_answer_id: string;
}

interface CommitVariables {
  questionId: string;
  audioS3Key: string;
  durationMs: number;
}

const CONNECT_LIBRARY_QUERY_KEY = "connect-library";

const fetchConnectLibrary = async (): Promise<ConnectLibraryResponse> => {
  const response = await fetchApi<ConnectLibraryResponse>({
    url: "/v2/activities/connect/library",
    method: "GET",
  });
  return response.data;
};

export const prefetchConnectLibrary = (queryClient: QueryClient): void => {
  void queryClient.prefetchQuery({
    queryKey: [CONNECT_LIBRARY_QUERY_KEY],
    queryFn: fetchConnectLibrary,
  });
};

const useLibraryMutationErrorToast = (fallback: string) => {
  const { toast } = useToast();
  return (error: Error & { response?: { data?: { error?: string } } }) => {
    toast({
      title: "Error",
      description: error?.response?.data?.error || fallback,
      variant: "destructive",
    });
  };
};

export const useConnectLibrary = () => {
  return useQuery<ConnectLibraryResponse>({
    queryKey: [CONNECT_LIBRARY_QUERY_KEY],
    queryFn: fetchConnectLibrary,
  });
};

// Presigned PUT URLs are short-lived; we don't useQuery-cache them.
export const fetchConnectLibraryPresignedUrl = async (
  questionId: string,
): Promise<PresignedUrlResponse> => {
  const response = await fetchApi<PresignedUrlResponse>({
    url: "/v2/activities/connect/library/presigned-url",
    method: "GET",
    params: { question_id: questionId },
  });
  return response.data;
};

export const useCommitConnectLibraryAnswer = () => {
  const queryClient = useQueryClient();
  const onError = useLibraryMutationErrorToast("Failed to save your recording");

  return useMutation<CommitResponse, Error, CommitVariables>({
    mutationKey: ["connect-library-commit"],
    mutationFn: async ({ questionId, audioS3Key, durationMs }) => {
      const response = await fetchApi<CommitResponse>({
        url: "/v2/activities/connect/library",
        method: "POST",
        data: {
          question_id: questionId,
          audio_s3_key: audioS3Key,
          duration_ms: durationMs,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CONNECT_LIBRARY_QUERY_KEY],
      });
    },
    onError,
  });
};

export const useDeleteConnectLibraryAnswer = () => {
  const queryClient = useQueryClient();
  const onError = useLibraryMutationErrorToast("Failed to delete recording");

  return useMutation<CommitResponse, Error, { libraryAnswerId: string }>({
    mutationKey: ["connect-library-delete"],
    mutationFn: async ({ libraryAnswerId }) => {
      const response = await fetchApi<CommitResponse>({
        url: `/v2/activities/connect/library/${libraryAnswerId}`,
        method: "DELETE",
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CONNECT_LIBRARY_QUERY_KEY],
      });
    },
    onError,
  });
};
